import type { ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { VendorProvider } from "@/contexts/VendorContext";
import { EventProvider } from "@/contexts/EventContext";
import { WeddingProvider } from "@/contexts/WeddingContext";
import { GuestProvider } from "@/contexts/GuestContext";
import { BudgetProvider } from "@/contexts/BudgetContext";
import { ChecklistProvider } from "@/contexts/ChecklistContext";
import { TimelineProvider } from "@/contexts/TimelineContext";
import { RunOfShowProvider } from "@/contexts/RunOfShowContext";
import { ContractProvider } from "@/contexts/ContractContext";
import { MoodboardProvider } from "@/contexts/MoodboardContext";
import { EventAssetsProvider } from "@/contexts/EventAssetsContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { BillingProvider } from "@/contexts/BillingContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { LanguageProvider } from "@/contexts/LanguageContext";

/**
 * Single composition root for every provider the app needs.
 *
 * Order matters: Theme has no dependencies, Auth must resolve before
 * Organization can fetch memberships. Vendor is scoped to the
 * ORGANIZATION (a directory reused across every wedding), so it nests
 * directly under OrganizationProvider, above Event/Wedding — unlike
 * Guest/Budget/Checklist/Timeline/RunOfShow/Contract/Moodboard, which are
 * scoped to the active wedding and nest below WeddingProvider instead.
 * Wedding itself is a compatibility read/write adapter over Event (see
 * WeddingContext) rather than an independent store. Keeping the nesting
 * here (rather than scattered across main.tsx) means adding a new
 * provider later is a one-line change.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider><BrowserRouter>
          <AuthProvider>
            <NotificationProvider><OrganizationProvider><BillingProvider>
              <VendorProvider>
                <EventProvider>
                  <WeddingProvider>
                    <GuestProvider>
                      <BudgetProvider>
                        <ChecklistProvider>
                          <TimelineProvider>
                            <RunOfShowProvider>
                              <ContractProvider>
                                <MoodboardProvider><EventAssetsProvider>
                                  <TooltipProvider delayDuration={200}>{children}</TooltipProvider>
                                </EventAssetsProvider></MoodboardProvider>
                              </ContractProvider>
                            </RunOfShowProvider>
                          </TimelineProvider>
                        </ChecklistProvider>
                      </BudgetProvider>
                    </GuestProvider>
                  </WeddingProvider>
                </EventProvider>
              </VendorProvider>
            </BillingProvider></OrganizationProvider></NotificationProvider>
          </AuthProvider>
        </BrowserRouter></LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
